import interpolate, { type TranslateFn } from "@app/_utils/interpolate";
import type { CountryCode } from "libphonenumber-js";
import parsePhoneNumberFromString, {
  getCountryCallingCode,
} from "libphonenumber-js";
import { z } from "zod";

import { getZipCodeRegexByMarketCode } from "@/config/market-configurations";
import type { MarketCode } from "@/config/markets/const";

export function getZipCodeOnlySchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    zipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
  });
}

export function getAddressSchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    firstName: z
      .string()
      .min(1, t("firstNameRequired") ?? "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z
      .string()
      .min(1, t("emailRequired") ?? "Email is required")
      .email(t("emailInvalid") ?? "Invalid email address"),
    company: z.string().optional(),
    phoneNumber: z
      .string()
      .min(1, t("phoneNumberRequired") ?? "Phone number is required"),
    line1: z
      .string()
      .min(1, t("streetRequired") ?? "Street address is required"),
    line2: z.string().optional(),
    city: z.string().min(1, t("cityRequired") ?? "City is required"),
    zipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
    country: z.string().optional(),
  });
}

export function getDeliveryAddressSchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    deliveryFirstName: z
      .string()
      .min(1, t("firstNameRequired") ?? "First name is required"),
    deliveryLastName: z
      .string()
      .min(1, t("lastNameRequired") ?? "Last name is required"),
    deliveryLine1: z
      .string()
      .min(1, t("streetRequired") ?? "Street address is required"),
    deliveryCity: z.string().min(1, t("cityRequired") ?? "City is required"),
    deliveryZipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
    deliveryCountry: z.string().optional(),
  });
}

/**
 * Phone numbers from these countries are accepted on every market, on top of
 * the market's own country. Swedish customers ordering to an address abroad
 * (e.g. a summer house in Spain) still want their Swedish mobile on the order.
 * Every number must be typed with its country code (+46 …), so the country is
 * always explicit and never guessed from the market.
 */
export const ALWAYS_ALLOWED_PHONE_COUNTRIES: readonly CountryCode[] = ["SE"];

/** "+46 …" or the "00 46 …" international dialling prefix. */
const INTERNATIONAL_PREFIX = /^\s*(\+|00)/;

/**
 * Uses the dedicated `phoneNumberMissingCountryCode` copy when it exists in
 * the dictionary, with the market's calling code available as an example.
 * Until that key is added to Contentful, fall back to the generic invalid
 * message: the "must start with +XX" copy would be wrong, since numbers from
 * ALWAYS_ALLOWED_PHONE_COUNTRIES are accepted on every market.
 */
function getMissingCountryCodeMessage(
  t: TranslateFn,
  countryCode: CountryCode,
) {
  const dedicated = t("phoneNumberMissingCountryCode", { strict: true });

  if (dedicated) {
    return interpolate(dedicated, {
      countryCallingCode: `+${getCountryCallingCode(countryCode)}`,
    });
  }

  return t("phoneNumberInvalid");
}

export function superRefinePhoneNumber(
  data: z.infer<ReturnType<typeof getAddressSchema>>,
  ctx: z.RefinementCtx,
  { marketCode, t }: { marketCode: MarketCode; t: TranslateFn },
) {
  const currentCountryCode = marketCode.toUpperCase() as CountryCode;

  if (!INTERNATIONAL_PREFIX.test(data.phoneNumber)) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message: getMissingCountryCodeMessage(t, currentCountryCode),
    });
    return;
  }

  const parsed = parsePhoneNumberFromString(
    data.phoneNumber,
    currentCountryCode,
  );

  if (!parsed) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message: t("phoneNumberInvalid"),
    });
  }

  if (!parsed?.isPossible()) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        t("phoneNumberInvalidLength") ??
        "Phone number is too short or too long",
    });
  }

  // Compare calling codes rather than the resolved country: libphonenumber
  // only sets `country` when the digits are valid for one of the countries
  // behind a shared calling code (+39, +1, +47, +358 ...), so a made-up
  // foreign number would otherwise carry no country and slip past this check.
  const allowedCallingCodes = new Set(
    [currentCountryCode, ...ALWAYS_ALLOWED_PHONE_COUNTRIES].map((country) =>
      getCountryCallingCode(country),
    ),
  );
  const isAllowedCallingCode =
    parsed !== undefined && allowedCallingCodes.has(parsed.countryCallingCode);

  if (parsed !== undefined && !isAllowedCallingCode) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        t("phoneNumberInvalidCountry") ?? "Invalid country for phone number",
    });
  }

  // A plausible-looking number that is not real for its country. Kept vague on
  // purpose: hinting at a specific calling code would be wrong on markets that
  // accept more than one.
  if (!parsed?.isValid() && isAllowedCallingCode) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message: t("phoneNumberInvalid"),
    });
  }
}

export function transformPhoneNumber(
  data: z.infer<ReturnType<typeof getAddressSchema>>,
) {
  const parsed = parsePhoneNumberFromString(
    data.phoneNumber,
    data.country?.toUpperCase() as CountryCode,
  );

  return {
    ...data,
    phoneNumber: parsed?.format("E.164") ?? data.phoneNumber,
  };
}
